import type { StyleSpecification } from 'maplibre-gl'

// MapLibre basemap configuration. All styles are open-source and
// self-hostable for air-gapped deployments.
//
// Vector tiles come from OpenFreeMap (https://openfreemap.org) — fully
// CC0 / open data, no API key, planet-scale CDN. The Positron (light) and
// Dark Matter (dark) themes are based on the OpenMapTiles schema and
// designed by CARTO; we use OpenFreeMap's hosted vector versions.
//
// To go air-gapped: download the planet PMTiles (or a regional extract)
// and host the styles locally — replace the URLs below with your tile
// server's address.
//
// Satellite and Hybrid stay raster: there is no open vector satellite
// imagery, so we use ESRI World Imagery (raster XYZ) for those.

const ESRI_WORLD_IMAGERY =
  'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'
const ESRI_TRANSPORTATION =
  'https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Transportation/MapServer/tile/{z}/{y}/{x}'

const ATTRIBUTION_ESRI =
  'Imagery © Esri, Maxar, Earthstar Geographics, USDA, USGS, AeroGRID, IGN, GIS Community'

// OpenFreeMap hosted style URLs. MapLibre's setStyle() accepts a URL
// directly and pulls the full style JSON (sources, sprites, glyphs).
const OFM_STYLE_DARK = 'https://tiles.openfreemap.org/styles/dark'
const OFM_STYLE_POSITRON = 'https://tiles.openfreemap.org/styles/positron'
const OFM_STYLE_LIBERTY = 'https://tiles.openfreemap.org/styles/liberty'

export type BaseStyleId = 'dark' | 'dark-hc' | 'streets' | 'satellite' | 'hybrid' | 'osm'

export const BASE_STYLE_LABELS: Record<BaseStyleId, string> = {
  dark: 'Dark Matter',
  'dark-hc': 'Dark+',
  streets: 'Streets',
  satellite: 'Satellite',
  hybrid: 'Hybrid',
  osm: 'Light',
}

// Whether a given basemap should receive the high-contrast paint patch.
// The viewer applies this after `setStyle` finishes loading.
export function isHighContrastDark(id: BaseStyleId): boolean {
  return id === 'dark-hc'
}

// Higher-contrast post-load tweaks for the OpenFreeMap dark style.
// We override paint properties of layers we know exist in OpenFreeMap's
// dark style (background, water, roads, labels). The try/catch around
// each setPaintProperty makes this resilient to upstream layer renames.
//
// Imported lazily by the viewer to keep the maplibre dependency out of
// non-map call sites.
import type { Map as MapLibreMap } from 'maplibre-gl'

export function applyHighContrastDarkPatch(map: MapLibreMap): void {
  const trySet = (
    layerId: string,
    prop: string,
    // biome-ignore lint/suspicious/noExplicitAny: maplibre's setPaintProperty value is broadly typed
    value: any
  ) => {
    try {
      map.setPaintProperty(layerId, prop, value)
    } catch {
      /* layer absent in this style version — ignore */
    }
  }

  // Deepen background to true black for strong contrast.
  trySet('background', 'background-color', '#020308')

  const layers = map.getStyle().layers ?? []
  for (const layer of layers) {
    const id = layer.id

    // Water — slightly brighter so it reads against the near-black land.
    if (layer.type === 'fill' && /water|ocean|sea|river/i.test(id)) {
      trySet(id, 'fill-color', '#0c1a26')
    }

    // Land / landuse / earth — keep dark but very subtly different so
    // boundaries still register against the background.
    if (layer.type === 'fill' && /landuse|landcover|park|wood|forest|earth|residential/i.test(id)) {
      trySet(id, 'fill-opacity', 0.55)
    }

    // Roads — push them lighter so they read clearly.
    if (layer.type === 'line' && /road|street|highway|motorway|tunnel|bridge|transport/i.test(id)) {
      if (/motorway|trunk/.test(id)) {
        trySet(id, 'line-color', '#7c8088')
      } else if (/primary|secondary/.test(id)) {
        trySet(id, 'line-color', '#5a5e66')
      } else {
        trySet(id, 'line-color', '#3f4248')
      }
    }

    // Boundaries — visible but not screaming.
    if (layer.type === 'line' && /boundary|admin/i.test(id)) {
      trySet(id, 'line-color', '#525864')
      trySet(id, 'line-opacity', 0.6)
    }

    // Labels — bright white with strong dark halo.
    if (layer.type === 'symbol') {
      trySet(id, 'text-color', '#f3f4f6')
      trySet(id, 'text-halo-color', 'rgba(0,0,0,0.92)')
      trySet(id, 'text-halo-width', 1.4)
    }
  }
}

export type BaseStyleSpec = string | StyleSpecification

const satelliteStyle = (): StyleSpecification => ({
  version: 8,
  name: 'satellite',
  glyphs: 'https://tiles.openfreemap.org/fonts/{fontstack}/{range}.pbf',
  sources: {
    satellite: {
      type: 'raster',
      tiles: [ESRI_WORLD_IMAGERY],
      tileSize: 256,
      attribution: ATTRIBUTION_ESRI,
    },
  },
  layers: [
    { id: 'background', type: 'background', paint: { 'background-color': '#000' } },
    { id: 'satellite', type: 'raster', source: 'satellite' },
  ],
})

const hybridStyle = (): StyleSpecification => ({
  version: 8,
  name: 'hybrid',
  glyphs: 'https://tiles.openfreemap.org/fonts/{fontstack}/{range}.pbf',
  sources: {
    satellite: {
      type: 'raster',
      tiles: [ESRI_WORLD_IMAGERY],
      tileSize: 256,
      attribution: ATTRIBUTION_ESRI,
    },
    transportation: {
      type: 'raster',
      tiles: [ESRI_TRANSPORTATION],
      tileSize: 256,
      attribution: ATTRIBUTION_ESRI,
    },
  },
  layers: [
    { id: 'background', type: 'background', paint: { 'background-color': '#000' } },
    { id: 'satellite', type: 'raster', source: 'satellite' },
    {
      id: 'transportation',
      type: 'raster',
      source: 'transportation',
      paint: { 'raster-opacity': 0.85 },
    },
  ],
})

export function getBaseStyle(id: BaseStyleId): BaseStyleSpec {
  switch (id) {
    case 'dark':
    case 'dark-hc':
      // 'dark-hc' uses the same OpenFreeMap source — the viewer patches
      // its paint properties after load to push contrast.
      return OFM_STYLE_DARK
    case 'streets':
      return OFM_STYLE_LIBERTY
    case 'osm':
      return OFM_STYLE_POSITRON
    case 'satellite':
      return satelliteStyle()
    case 'hybrid':
      return hybridStyle()
  }
}
